import React from "react";

export type OpdReceiptData = {
  clinicName?: string;
  clinicTagline?: string;
  regNo?: string;
  billNumber?: string;
  billDate?: string;
  patientName?: string;
  uhid?: string;
  consultantName?: string;
  particulars?: Array<{ description: string; amount: number }>;
  totalAmount?: number;
  paidAmount?: number;
  paymentModeBreakdown?: Record<string, number>;
  remarks?: string;
  amountInWords?: string;
  clinicAddress?: string;
};

const OpdReceipt: React.FC<{ data: OpdReceiptData }> = ({ data }) => {
  return (
    <div className="max-w-[800px] mx-auto border border-gray-800 p-6 bg-white">
      <div className="text-center mb-4">
        <div className="text-lg font-bold">{data.clinicName ?? "Clinic"}</div>
        {data.clinicTagline && (
          <div className="text-xs text-gray-600">{data.clinicTagline}</div>
        )}
        {data.regNo && (
          <div className="text-xs text-right">Reg. No.: {data.regNo}</div>
        )}
        <div className="text-sm font-bold underline mt-3">OPD Bill Cum Receipt</div>
      </div>

      <div className="flex justify-between flex-wrap gap-2 mb-2 text-sm">
        <div className="flex gap-6">
          <span><strong>Bill No.:</strong> {data.billNumber ?? "-"}</span>
          <span><strong>Date:</strong> {data.billDate ?? "-"}</span>
        </div>
        <div className="flex gap-6">
          <span><strong>Patient:</strong> {data.patientName ?? "-"}</span>
          <span><strong>UHID:</strong> {data.uhid ?? "-"}</span>
        </div>
      </div>
      <div className="flex gap-6 mb-4 text-sm">
        <span><strong>Consultant:</strong> {data.consultantName ?? "-"}</span>
      </div>

      <table className="w-full border-collapse border border-gray-800 my-4 text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-800 p-2 w-12 text-left">Sr.</th>
            <th className="border border-gray-800 p-2 text-left">Particulars</th>
            <th className="border border-gray-800 p-2 w-28 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {(data.particulars ?? []).map((row, idx) => (
            <tr key={idx}>
              <td className="border border-gray-800 p-2">{idx + 1}</td>
              <td className="border border-gray-800 p-2">{row.description}</td>
              <td className="border border-gray-800 p-2 text-right">
                {(row.amount ?? 0).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-between font-bold my-3 text-sm">
        <span>TOTAL: {(data.totalAmount ?? 0).toFixed(2)}</span>
        <span>PAID: {(data.paidAmount ?? 0).toFixed(2)}</span>
      </div>

      <div className="flex flex-wrap gap-6 my-3 text-sm">
        {Object.entries(data.paymentModeBreakdown ?? {}).map(([mode, amt]) => (
          <span key={mode}>
            <strong>{mode}:</strong> {amt > 0 ? amt.toFixed(2) : ""}
          </span>
        ))}
      </div>

      {data.remarks && (
        <div className="my-2 text-sm">Remarks : {data.remarks}</div>
      )}
      <div className="my-2 text-sm italic">
        Rs. in words : {data.amountInWords ?? ""}
      </div>
      {data.clinicAddress && (
        <div className="mt-6 text-right text-xs text-gray-600">
          {data.clinicAddress}
        </div>
      )}
    </div>
  );
};

export default OpdReceipt;
